<template>
    <div class="mt-6">
        <Label :label="'Band Members'" />
        <div class="mt-4">
            <!-- Current Members -->
            <div v-if="currentMembers.length > 0" class="mb-6">
                <h4 class="text-sm font-medium text-gray-700 mb-3">Current Members</h4>
                <div class="space-y-3">
                    <div v-for="member in currentMembers" :key="member.id" 
                         class="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div class="flex items-center space-x-3">
                            <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <span class="text-green-600 text-sm font-medium">{{ member.name.charAt(0).toUpperCase() }}</span>
                            </div>
                            <div>
                                <div class="font-medium text-gray-900">{{ member.name }}</div>
                                <div class="text-sm text-gray-500">{{ member.role || 'Member' }}</div>
                            </div>
                        </div>
                        <div class="text-right">
                            <div class="text-sm text-green-600 font-medium">Active</div>
                            <div class="text-xs text-gray-500">
                                Since {{ formatDate(member.startDate) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Timeline for All Members -->
            <div v-if="allMembers.length > 0" class="mb-6">
                <h4 class="text-sm font-medium text-gray-700 mb-3">Member Timeline</h4>
                <div class="relative">
                    <!-- Timeline line -->
                    <div class="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    
                    <div class="space-y-4">
                        <div v-for="member in sortedMembers" :key="member.id" 
                             class="relative flex items-start space-x-4">
                            <!-- Timeline dot -->
                            <div class="flex-shrink-0 w-8 h-8 rounded-full border-2 border-white shadow-sm flex items-center justify-center"
                                 :class="getMemberStatusClass(member)">
                                <span class="text-xs font-medium" :class="getMemberStatusTextClass(member)">
                                    {{ member.name.charAt(0).toUpperCase() }}
                                </span>
                            </div>
                            
                            <!-- Member info -->
                            <div class="flex-1 min-w-0">
                                <div class="flex items-center justify-between">
                                    <div>
                                        <div class="font-medium text-gray-900">{{ member.name }}</div>
                                        <div class="text-sm text-gray-500">{{ member.role || 'Member' }}</div>
                                    </div>
                                    <div class="text-right">
                                        <div class="text-sm font-medium" :class="getMemberStatusTextClass(member)">
                                            {{ getMemberStatus(member) }}
                                        </div>
                                        <div class="text-xs text-gray-500">
                                            {{ formatDateRange(member.startDate, member.endDate) }}
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Participation period -->
                                <div class="mt-2 text-xs text-gray-400">
                                    <span v-if="member.startDate && member.endDate">
                                        {{ formatDate(member.startDate) }} - {{ formatDate(member.endDate) }}
                                    </span>
                                    <span v-else-if="member.startDate">
                                        {{ formatDate(member.startDate) }} - Present
                                    </span>
                                    <span v-else>
                                        Unknown period
                                    </span>
                                </div>
                                
                                <!-- Additional info -->
                                <div v-if="member.instruments || member.notes" class="mt-2 text-xs text-gray-500">
                                    <span v-if="member.instruments">{{ member.instruments }}</span>
                                    <span v-if="member.instruments && member.notes"> • </span>
                                    <span v-if="member.notes">{{ member.notes }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- No members message -->
            <div v-if="allMembers.length === 0" class="text-center py-6 text-gray-500">
                <div class="text-sm">No member information available</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps, defineExpose } from 'vue';

// Props
const props = defineProps({
    members: {
        type: Array,
        default: () => []
    },
    showTimeline: {
        type: Boolean,
        default: true
    }
});

// Computed properties
const currentMembers = computed(() => {
    return props.members.filter(member => member.ended === false || (!member.endDate && !member.ended));
});

const allMembers = computed(() => {
    return props.members;
});

const sortedMembers = computed(() => {
    return [...props.members].sort((a, b) => {
        // Sort by start date (newest first), then by name
        const dateA = a.startDate ? new Date(a.startDate) : new Date(0);
        const dateB = b.startDate ? new Date(b.startDate) : new Date(0);
        
        if (dateA.getTime() !== dateB.getTime()) {
            return dateB.getTime() - dateA.getTime();
        }
        
        return a.name.localeCompare(b.name);
    });
});

// Methods
const getMemberStatus = (member) => {
    // Check if member has ended (from MusicBrainz relation data)
    if (member.ended === false || (!member.endDate && !member.ended)) {
        return 'Active';
    }
    return 'Former';
};

const getMemberStatusClass = (member) => {
    if (member.ended === false || (!member.endDate && !member.ended)) {
        return 'bg-green-100 border-green-300';
    }
    return 'bg-gray-100 border-gray-300';
};

const getMemberStatusTextClass = (member) => {
    if (member.ended === false || (!member.endDate && !member.ended)) {
        return 'text-green-600';
    }
    return 'text-gray-600';
};

const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short'
        });
    } catch (error) {
        return dateString;
    }
};

const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return 'Unknown period';
    if (!startDate) return `Until ${formatDate(endDate)}`;
    if (!endDate) return `Since ${formatDate(startDate)}`;
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

// Expose methods for parent component
defineExpose({
    currentMembers,
    allMembers,
    sortedMembers
});
</script> 